/* Каталог «Рилса» из формы сервера в таблицах шаблона (план рилса §2.2): чистое построение и запись на месте.
   Движок (features/reels/engine.ts) держит свои таблицы в замыканиях: CATS, CATN, NEED, TYPES, CAST, CAST_ND, PRESETS, PL, LEGACY —
   их нельзя заменить, только переписать (splice, удалить ключи и поставить новые). Данные — только из формы: разделы, «кто нужно»,
   типы с ценами, роли (свои у шаблона, иначе по виду «кто нужно»), шаблоны (допустимые формой id), пакет, числа предложения.
   Роли приводятся к модели роли шаблона (RL bot-reels.js, castOf bot-people.js): {id, name, hint, kinds, min, max, me, allowChar, chars, g}.
   Подписи интерфейса (бейджи «Хит» / «Новое», сюжет пакета) идут словарём профиля — их даёт вызывающий (words). */
import type { StudioForm } from '../studio/studio-api';
import { normalizeRole, type ReelsRole } from './reels-cast';
import { allowedIds, canPack } from './reels-inputs';
import { objectWords, type ReelsPolicy, type ReelsTemplate } from './reels-policy';

/** Роль в модели шаблона */
export interface TplRole {
  id: string;
  name: string;
  hint: string;
  kinds: string[];
  min: number;
  max: number;
  me: boolean;
  allowChar: boolean;
  chars: 'all' | string | string[];
  g?: 'm' | 'f';
}
/** Шаблон в модели шаблона (PRESETS bot-reels.js) + то, что знает только сервер: кадры примера, своя цена */
/** Накладки, у которых текст — надпись поверх обложки, а не служебная пометка */
const TEXT_OVERLAYS = new Set(['mag', 'bars', 'pola']);
export interface TplPreset {
  id: string;
  cat: string;
  i: number;
  n: string;
  sub: string;
  nd: string;
  min: number;
  max: number;
  d: number;
  t: string;
  b: string;
  ov: string;
  tx: string;
  f: string;
  frames: string[];
  price: number | null;
  more: number | null;
}
export interface TplNeed {
  ppl?: number;
  lb: string;
  of?: string;
  tip?: string;
}
export interface Tables {
  CATS: Array<[string, string]>;
  NEED: Record<string, TplNeed>;
  TYPES: Record<string, { n: string; price: number }>;
  CAST: Record<string, TplRole[]>;
  CAST_ND: Record<string, TplRole[]>;
  PL: TplPreset[];
  LEGACY: Record<string, { c?: string; p?: string }>;
  offer: { vars?: number; packN?: number; multiFrom?: number; multiOff?: number; maxPh: number };
}
/** Таблицы движка (те же объекты, что в его замыканиях) */
export interface EngineTables {
  TYPES: Record<string, { n: string; price: number }>;
  CATS: Array<[string, string]>;
  CATN: Record<string, string>;
  NEED: Record<string, TplNeed>;
  CAST: Record<string, TplRole[]>;
  CAST_ND: Record<string, TplRole[]>;
  PRESETS: Record<string, TplPreset>;
  PL: TplPreset[];
  LEGACY: Record<string, { c?: string; p?: string }>;
}
export interface CatalogWords {
  hit: string;
  fresh: string;
  /** Сюжет пакета: «10 разных сюжетов с вами за один заказ» */
  packSub(count: number): string;
  /** Свои подписи вида фото («Фото товара», «товара», совет), когда документ их не дал — словарём профиля */
  t?(value: string): string;
}

/** Роль сервера (snake_case) или записи шаблона → модель роли шаблона; не роль — null */
export function tplRole(raw: unknown): TplRole | null {
  const role: ReelsRole | null = normalizeRole(raw, '');
  if (!role) return null;
  const chars = Array.isArray(role.chars) ? role.chars.map(String) : role.chars === 'all' ? 'all' : role.chars.collection;
  return {
    id: role.id,
    name: role.name,
    hint: role.hint,
    kinds: role.kinds.slice(),
    min: role.min,
    max: role.max,
    me: role.selfDefault,
    allowChar: role.allowChar,
    chars,
    ...(role.gender ? { g: role.gender } : {}),
  };
}
const roles = (list: unknown[] | null | undefined): TplRole[] => (list ?? []).map(tplRole).filter((x): x is TplRole => !!x);
const sum = (list: TplRole[], key: 'min' | 'max') => list.reduce((a, r) => a + r[key], 0);
const word = (b: ReelsTemplate['badge'], w: CatalogWords) => (b === 'hit' ? w.hit : b === 'new' ? w.fresh : '');

/** Таблицы по политике формы (reelsPolicy) */
export function tablesFrom(form: StudioForm, policy: ReelsPolicy, words: CatalogWords): Tables {
  const maxPh = Math.max(1, Math.min(20, policy.maxObjectPhotos));
  const allowed = new Set(allowedIds(form));
  const list = policy.templates.filter(t => !allowed.size || allowed.has(t.id));
  /* разделы: документа, иначе — по шаблонам; шаблон без раздела — в первый */
  const cats: Array<[string, string]> = policy.categories.map(c => [c.id, c.title]);
  for (const t of list) if (t.categoryId && !cats.some(c => c[0] === t.categoryId)) cats.push([t.categoryId, t.categoryId]);
  const tw = words.t ?? ((value: string) => value);
  const NEED: Tables['NEED'] = {};
  for (const [key, n] of Object.entries(policy.needs)) {
    const w = objectWords[key];
    NEED[key] = n.people
      ? { ppl: 1, lb: n.label }
      : { lb: n.label || tw(w?.label || objectWords.pics.label), of: n.of || (w?.of ? tw(w.of) : ''), tip: n.tip || (w?.tip ? tw(w.tip) : '') };
  }
  const TYPES: Tables['TYPES'] = {};
  for (const [key, t] of Object.entries(policy.types)) TYPES[key] = { n: t.title, price: t.price ?? 0 };
  const CAST_ND: Tables['CAST_ND'] = {};
  for (const [key, list2] of Object.entries(policy.castByNeed)) CAST_ND[key] = roles(list2);
  const CAST: Tables['CAST'] = {};
  const PL: TplPreset[] = [];
  for (const t of list) {
    const own = t.cast ? roles(t.cast) : null;
    const cast = own ?? CAST_ND[t.need] ?? null;
    /* «кто нужно» без записи документа: люди — у шаблона с ролями, иначе фото (вид из ключа) */
    if (!NEED[t.need]) {
      if (cast?.length) NEED[t.need] = { ppl: 1, lb: '' };
      else {
        const w = objectWords[t.need] ?? objectWords.pics;
        NEED[t.need] = { lb: tw(w.label), of: w.of ? tw(w.of) : '', tip: tw(w.tip) };
      }
    }
    const people = !!NEED[t.need].ppl;
    if (people && !cast?.length) continue; /* роли шаблона неизвестны — сыграть его нельзя */
    if (own?.length) CAST[t.id] = own;
    if (!TYPES[t.type]) TYPES[t.type] = { n: '', price: t.price ?? 0 };
    const photos = t.photos ?? { min: 1, max: maxPh };
    PL.push({
      id: t.id,
      cat: t.categoryId || cats[0]?.[0] || '',
      i: PL.length,
      n: t.title,
      sub: t.plot,
      nd: t.need,
      min: people ? sum(cast!, 'min') : Math.min(maxPh, photos.min),
      max: people ? sum(cast!, 'max') : Math.min(maxPh, photos.max),
      d: t.durationS ?? 0,
      t: t.type,
      b: word(t.badge, words),
      ov: t.overlay,
      /* подписи-надписи поверх обложки («ГЕРОЙ НОМЕРА», «В ГЛАВНОЙ РОЛИ») — нет (владелец 02.11.2026); «до / после», дата, таймкод остаются */
      tx: TEXT_OVERLAYS.has(t.overlay) ? '' : t.overlayText,
      f: '',
      frames: t.frames.slice(),
      price: t.price,
      more: t.morePrice,
    });
  }
  /* пакет: форма предлагает режим pack и документ — offer.pack; роли — «с вами» (cast_by_need.me) */
  if (canPack(form, policy) && policy.pack && PL.length) {
    const me = CAST_ND.me ?? [];
    TYPES.x = { n: '', price: policy.pack.price ?? 0 };
    const foryou = cats.find(c => c[0] === 'foryou')?.[0] ?? cats[0]?.[0] ?? '';
    PL.push({
      id: 'pack',
      cat: foryou,
      i: PL.length,
      n: policy.pack.title,
      sub: words.packSub(policy.pack.count),
      nd: 'me',
      min: me.length ? sum(me, 'min') : 1,
      max: me.length ? sum(me, 'max') : 1,
      d: policy.pack.durationS ?? 0,
      t: 'x',
      b: word(policy.pack.badge, words),
      ov: '',
      tx: '',
      f: '',
      frames: [],
      price: policy.pack.price,
      more: null,
    });
  }
  if (!NEED.me) NEED.me = { ppl: 1, lb: '' };
  const presets = new Set(PL.map(p => p.id));
  const catIds = new Set(cats.map(c => c[0]));
  /* старые входы студии (preset: people | product | series | timelapse | family | slides) — только к тому, что есть в каталоге */
  const LEGACY: Tables['LEGACY'] = {};
  const legacy: Record<string, { c?: string; p?: string }> = {
    people: { c: 'foryou' },
    product: { p: 'mp' },
    series: { p: 'alive' },
    timelapse: { p: 'tlapse' },
    family: { p: 'kino' },
    slides: { p: 'polar' },
  };
  for (const [key, v] of Object.entries(legacy)) if ((v.c && catIds.has(v.c)) || (v.p && presets.has(v.p))) LEGACY[key] = v;
  const multi = policy.multi;
  return {
    CATS: cats,
    NEED,
    TYPES,
    CAST,
    CAST_ND,
    PL,
    LEGACY,
    offer: {
      /* без числа в предложении — один ролик в заказе (решение 29.10.2026: сцена дорогая и долгая, ещё вариант — дозаказом) */
      vars: policy.variants ?? 1,
      ...(policy.pack ? { packN: policy.pack.count } : {}),
      ...(multi ? { multiFrom: Number.isFinite(multi.discountFrom) ? multi.discountFrom : 1e9, multiOff: multi.discountPct / 100 } : {}),
      maxPh,
    },
  };
}

const refill = <T>(target: Record<string, T>, source: Record<string, T>) => {
  for (const key of Object.keys(target)) delete target[key];
  Object.assign(target, source);
};
/** Записать таблицы в объекты движка на месте (ссылки замыканий остаются) */
export function writeTables(e: EngineTables, t: Tables) {
  e.CATS.splice(0, e.CATS.length, ...t.CATS);
  refill(e.CATN, Object.fromEntries(t.CATS));
  refill(e.NEED, t.NEED);
  refill(e.TYPES, t.TYPES);
  refill(e.CAST, t.CAST);
  refill(e.CAST_ND, t.CAST_ND);
  e.PL.splice(0, e.PL.length, ...t.PL);
  refill(e.PRESETS, Object.fromEntries(t.PL.map(p => [p.id, p])));
  refill(e.LEGACY, t.LEGACY);
}
/** Пустые таблицы (до формы и в демо-данных шаблона, которых сервер не знает) */
export const emptyTables = (): Tables => ({ CATS: [], NEED: { me: { ppl: 1, lb: '' } }, TYPES: {}, CAST: {}, CAST_ND: {}, PL: [], LEGACY: {}, offer: { maxPh: 20 } });
